import { apiGet, apiPatch, apiPost, apiPut } from "@/lib/apiClient";
import type {
  OrgAgent,
  OrgConnection,
  OrgSchedule,
  OrgSharedInbox,
} from "@/lib/agentsDemoData";
import { migrateOrgAgents } from "@/lib/agentsDemoData";

export type WorkforceRosterPayload = {
  orgId: string;
  agents: OrgAgent[];
  connections: OrgConnection[];
  schedules: OrgSchedule[];
  sharedInboxes: OrgSharedInbox[];
  maxAddon: boolean;
  empty: boolean;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string | null;
};

export type WorkforceRosterWriteBody = {
  agents: OrgAgent[];
  connections: OrgConnection[];
  schedules: OrgSchedule[];
  sharedInboxes: OrgSharedInbox[];
  maxAddon: boolean;
};

function normalizeRoster(raw: WorkforceRosterPayload | null | undefined): WorkforceRosterPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const orgId = typeof raw.orgId === "string" ? raw.orgId.trim() : "";
  if (!orgId) return null;
  const agents = migrateOrgAgents(Array.isArray(raw.agents) ? raw.agents : []);
  const connections = Array.isArray(raw.connections) ? raw.connections : [];
  const schedules = Array.isArray(raw.schedules) ? raw.schedules : [];
  const sharedInboxes = Array.isArray(raw.sharedInboxes) ? raw.sharedInboxes : [];
  return {
    orgId,
    agents,
    connections,
    schedules,
    sharedInboxes,
    maxAddon: Boolean(raw.maxAddon),
    empty: Boolean(
      raw.empty ??
        (agents.length === 0 &&
          connections.length === 0 &&
          schedules.length === 0 &&
          sharedInboxes.length === 0),
    ),
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    updatedBy: raw.updatedBy ?? null,
  };
}

type RosterEnvelope = { ok?: boolean; roster?: WorkforceRosterPayload; error?: string };

export async function fetchWorkforceRoster(): Promise<
  { ok: true; roster: WorkforceRosterPayload } | { ok: false; error: string; status: number }
> {
  const res = await apiGet<RosterEnvelope>("/api/workforce/roster");
  if (!res.ok || !res.data?.roster) {
    return {
      ok: false,
      error: res.error || res.data?.error || "Could not load workforce roster.",
      status: res.status,
    };
  }
  const roster = normalizeRoster(res.data.roster);
  if (!roster) {
    return { ok: false, error: "Invalid roster payload.", status: res.status };
  }
  return { ok: true, roster };
}

export async function saveWorkforceRoster(body: WorkforceRosterWriteBody): Promise<
  { ok: true; roster: WorkforceRosterPayload } | { ok: false; error: string; status: number }
> {
  const res = await apiPut<RosterEnvelope>("/api/workforce/roster", body);
  if (!res.ok || !res.data?.roster) {
    return {
      ok: false,
      error: res.error || res.data?.error || "Could not save workforce roster.",
      status: res.status,
    };
  }
  const roster = normalizeRoster(res.data.roster);
  if (!roster) {
    return { ok: false, error: "Invalid roster payload.", status: res.status };
  }
  return { ok: true, roster };
}

export async function importWorkforceRoster(body: WorkforceRosterWriteBody): Promise<
  | { ok: true; roster: WorkforceRosterPayload; imported: boolean }
  | { ok: false; error: string; status: number; roster?: WorkforceRosterPayload }
> {
  const res = await apiPost<RosterEnvelope & { imported?: boolean }>(
    "/api/workforce/roster/import",
    body,
  );
  const roster = normalizeRoster(res.data?.roster);
  if (!res.ok) {
    return {
      ok: false,
      error: res.error || res.data?.error || "Could not import local roster.",
      status: res.status,
      ...(roster ? { roster } : {}),
    };
  }
  if (!roster) {
    return { ok: false, error: "Invalid roster payload.", status: res.status };
  }
  return { ok: true, roster, imported: Boolean(res.data?.imported) };
}

export async function createWorkforceAgent(agent: OrgAgent): Promise<
  | { ok: true; agent: OrgAgent; roster: WorkforceRosterPayload }
  | { ok: false; error: string; status: number }
> {
  const res = await apiPost<{ ok?: boolean; agent?: OrgAgent; roster?: WorkforceRosterPayload; error?: string }>(
    "/api/workforce/roster/agents",
    { agent },
  );
  const roster = normalizeRoster(res.data?.roster);
  if (!res.ok || !res.data?.agent || !roster) {
    return {
      ok: false,
      error: res.error || res.data?.error || "Could not create agent.",
      status: res.status,
    };
  }
  return { ok: true, agent: res.data.agent, roster };
}

export async function updateWorkforceAgent(
  agentId: string,
  patch: Partial<OrgAgent>,
): Promise<
  | { ok: true; agent: OrgAgent; roster: WorkforceRosterPayload }
  | { ok: false; error: string; status: number }
> {
  const res = await apiPatch<{ ok?: boolean; agent?: OrgAgent; roster?: WorkforceRosterPayload; error?: string }>(
    `/api/workforce/roster/agents/${encodeURIComponent(agentId)}`,
    patch,
  );
  const roster = normalizeRoster(res.data?.roster);
  if (!res.ok || !res.data?.agent || !roster) {
    return {
      ok: false,
      error: res.error || res.data?.error || "Could not update agent.",
      status: res.status,
    };
  }
  return { ok: true, agent: res.data.agent, roster };
}

export async function archiveWorkforceAgent(agentId: string): Promise<
  | { ok: true; agent: OrgAgent; roster: WorkforceRosterPayload }
  | { ok: false; error: string; status: number }
> {
  const res = await apiPost<{ ok?: boolean; agent?: OrgAgent; roster?: WorkforceRosterPayload; error?: string }>(
    `/api/workforce/roster/agents/${encodeURIComponent(agentId)}/archive`,
    {},
  );
  const roster = normalizeRoster(res.data?.roster);
  if (!res.ok || !res.data?.agent || !roster) {
    return {
      ok: false,
      error: res.error || res.data?.error || "Could not archive agent.",
      status: res.status,
    };
  }
  return { ok: true, agent: res.data.agent, roster };
}
